import {
  CompactKeyList,
  DateCell,
  EstimatedDeliveryDate,
  HoverableLabel,
  LocationCell,
  NavigateButton,
  TruncatedText,
} from 'components/common';
import { WifiUnknownIcon } from 'components/common/Icons';
import { RowLayout } from 'components/core/ListTable/types';
import { scopedT } from 'utils/functions/Translations';

import { Pill, reactIcons as Icons } from '@project44/manifest-ds';
import {
  MilestoneType,
  ModeType,
  RankedIdentifiersType,
  ShipmentEntity,
  StatusType,
  TrackingStatusEventType,
} from '@project44-models/shipment-list';

import {
  ARRIVAL_SORT_OPTIONS,
  DEPARTURE_SORT_OPTIONS,
  PLANNED_SORT_OPTIONS_ARRIVAL,
  PLANNED_SORT_OPTIONS_DEPARTURE,
  POD_ARRIVAL_SORT_OPTIONS,
  POD_PLANNED_SORT_OPTIONS_ARRIVAL,
  POO_DEPARTURE_SORT_OPTIONS,
  POO_PLANNED_SORT_OPTIONS_DEPARTURE,
} from './MasterSort';
import { PATHS } from './Routes';

type IdentifierType = keyof typeof RankedIdentifiersType;

export const IDENTIFIER_TITLE_MAP: Record<IdentifierType, string> = {
  BILL_OF_LADING: 'Bill of Lading',
  BOOKING_NUMBER: 'Booking',
  CONTAINER_ID: 'Container',
  ORDER: 'Order',
  PRO: 'PRO number',
  TRACKING_NUMBER: 'Tracking',
  AIR_WAYBILL: 'Air Waybill',
  HOUSE_AIR_WAYBILL: 'House Air Waybill',
  RAIL_CAR_ID: 'Rail Car ID',
  SERIAL_NUMBER: 'Serial Number',
  WAYBILL: 'Waybill',
  TRAIN_NUMBER: 'Train Number',
  VEHICLE_IDENTIFICATION_NUMBER: 'Vehicle identification number',
  WAGON_ID: 'Wagon ID',
  UNKNOWN: 'Unknown',
};

export const IDENTIFIER_TAG_MAP: Record<IdentifierType, string> = {
  BILL_OF_LADING: 'BOL',
  BOOKING_NUMBER: 'BKG',
  CONTAINER_ID: 'CONT',
  ORDER: 'ORDER',
  PRO: 'PRO',
  TRACKING_NUMBER: 'TRK',
  AIR_WAYBILL: 'MAWB',
  HOUSE_AIR_WAYBILL: 'HAWB',
  RAIL_CAR_ID: 'RAIL',
  SERIAL_NUMBER: 'SN',
  WAYBILL: 'WB',
  TRAIN_NUMBER: 'TRAIN',
  VEHICLE_IDENTIFICATION_NUMBER: 'VIN',
  WAGON_ID: 'WAG',
  UNKNOWN: 'UNK',
};
export const STATUS_ICON_MAP: Record<
  Exclude<StatusType, 'IN_TRANSIT'>,
  JSX.Element
> = {
  AT_STOP: <Icons.Location data-testid="location-icon" />,
  COMPLETED: <Icons.Check data-testid="check-icon" />,
  SCHEDULED: <Icons.Calendar data-testid="calendar-icon" />,
  PENDING: <Icons.Rewind data-testid="rewind-icon" />,
  CUSTOMS_HOLD: <Icons.Rewind data-testid="rewind-icon" />,
};

export const STATUS_TITLE_MAP: Record<StatusType, string> = {
  AT_STOP: 'At Stop',
  COMPLETED: 'Completed',
  IN_TRANSIT: 'In transit',
  PENDING: 'Unknown',
  SCHEDULED: 'Scheduled',
  CUSTOMS_HOLD: 'Custom Hold',
};

export const MODE_ICON_MAP: Record<ModeType, JSX.Element> = {
  AIR: <Icons.AirplaneFilled data-testid="plane-icon" />,
  LTL: <Icons.TruckHalfFull data-testid="truck-half-icon" />,
  OCEAN: <Icons.Boat data-testid="boat-icon" />,
  PARCEL: <Icons.Parcel data-testid="parcel-icon" />,
  RAIL: <Icons.TrainFilled data-testid="train-icon" />,
  TRUCKLOAD: <Icons.Truck data-testid="truck-icon" />,
  UNKNOWN: <Icons.Rewind data-testid="rewind-icon" />,
};

export const CONNECTIVITY_ICON_MAP: Record<
  TrackingStatusEventType,
  JSX.Element
> = {
  DRIVER_DENY_TRACKING: <Icons.WifiError data-testid="wifi-error-icon" />,
  MISSING_EQUIPMENT_IDENTIFIER: <WifiUnknownIcon />,
  TRACKING_COMPLETE: <Icons.WifiEmpty data-testid="wifi-empty-icon" />,
  TRACKING_END_BY_USER: <Icons.WifiEmpty data-testid="wifi-empty-icon" />,
  TRACKING_END_DUE_TO_TIMEOUT: (
    <Icons.WifiEmpty data-testid="wifi-empty-icon" />
  ),
  TRACKING_FAILED: <Icons.WifiError data-testid="wifi-error-icon" />,
  TRACKING_INITIATE: <Icons.WifiEmpty data-testid="wifi-empty-icon" />,
  TRACKING_START: <Icons.WifiFilled data-testid="wifi-filled-icon" />,
  TRACKING_STOP: <Icons.WifiEmpty data-testid="wifi-empty-icon" />,
  UNKNOWN: <Icons.WifiEmpty data-testid="wifi-empty-icon" />,
  UNKNOWN_EQUIPMENT_IDENTIFIER: <WifiUnknownIcon />,
};

export const CONNECTIVITY_TITLE_MAP: Record<TrackingStatusEventType, string> = {
  DRIVER_DENY_TRACKING: 'Driver denied',
  MISSING_EQUIPMENT_IDENTIFIER: 'Missing equipment ID',
  TRACKING_COMPLETE: 'Tracking complete',
  TRACKING_END_BY_USER: 'Ended by user',
  TRACKING_END_DUE_TO_TIMEOUT: 'Tracking timeout',
  TRACKING_FAILED: 'Tracking failed',
  TRACKING_INITIATE: 'Tracking initiate',
  TRACKING_START: 'Tracking started',
  TRACKING_STOP: 'Tracking stop',
  UNKNOWN: 'Unknown',
  UNKNOWN_EQUIPMENT_IDENTIFIER: 'Unknown equipment ID',
};

const buildEtaOrActCellForMilestone = (milestoneType: MilestoneType) => {
  return (shipment: ShipmentEntity) => {
    const milestone = shipment.getMilestoneByType(milestoneType);
    return (
      <EstimatedDeliveryDate
        actualDateTime={milestone?.actualDateTime}
        estimatedDateTime={milestone?.estimateDateTime}
        deltaEstimatedTime={milestone?.getDeltaEta()}
      />
    );
  };
};

const buildDateCellForMilestone = (milestoneType: MilestoneType) => {
  return (shipment: ShipmentEntity) => {
    const milestone = shipment.getMilestoneByType(milestoneType);
    return <DateCell dateTime={milestone?.plannedDateTime} />;
  };
};

const buildLocationCellForMilestone = (milestoneType: MilestoneType) => {
  return (shipment: ShipmentEntity) => {
    const milestone = shipment.getMilestoneByType(milestoneType);
    return (
      <LocationCell
        locationName={milestone?.stop?.location?.name || '-'}
        cityState={milestone?.stop?.location?.address?.getCityState() || '-'}
      />
    );
  };
};

const t = scopedT();

export const rowLayout: RowLayout<ShipmentEntity> = [
  {
    id: 'shipment-id',
    element: (shipment) => {
      return (
        <HoverableLabel
          label={
            IDENTIFIER_TAG_MAP[
              shipment.primaryShipmentIdentifier?.type || 'UNKNOWN'
            ]
          }
          labelTooltip={
            IDENTIFIER_TITLE_MAP[
              shipment.primaryShipmentIdentifier?.type || 'UNKNOWN'
            ]
          }
          text={shipment.primaryShipmentIdentifier?.value || ''}
        />
      );
    },
  },
  {
    id: 'current-state',
    element: (shipment) => {
      const { activeSegmentMode } = shipment;
      const currentStatus = shipment.getCurrentStatus();
      const Icon =
        currentStatus === 'IN_TRANSIT'
          ? MODE_ICON_MAP[activeSegmentMode || 'UNKNOWN']
          : STATUS_ICON_MAP[currentStatus];
      return <Pill icon={Icon} label={STATUS_TITLE_MAP[currentStatus]} />;
    },
  },
  {
    id: 'current-carrier',
    element: (shipment) => (
      <TruncatedText>{shipment.getCarrier()}</TruncatedText>
    ),
  },
  {
    id: 'origin',
    element: ({ originLocation }) => (
      <LocationCell
        locationName={originLocation?.name || '-'}
        cityState={originLocation?.address?.getCityState() || ''}
      />
    ),
  },
  {
    id: 'est-act-departure',
    element: buildEtaOrActCellForMilestone('DEPARTURE_FROM_ABSOLUTE_ORIGIN'),
  },
  {
    id: 'planned-departure',
    element: buildDateCellForMilestone('DEPARTURE_FROM_ABSOLUTE_ORIGIN'),
  },
  {
    id: 'destination',
    element: ({ destinationLocation }) => (
      <LocationCell
        locationName={destinationLocation?.name || '-'}
        cityState={destinationLocation?.address?.getCityState() || ''}
      />
    ),
  },
  {
    id: 'est-act-arrival',
    element: (shipment) => {
      const { deliveryDateTime, deliveryEstimateDateTime } = shipment;
      return (
        <EstimatedDeliveryDate
          actualDateTime={deliveryDateTime}
          estimatedDateTime={deliveryEstimateDateTime}
          deltaEstimatedTime={shipment.getDeltaEta()}
        />
      );
    },
  },
  {
    id: 'planned-arrival',
    element: (shipment) => (
      <DateCell dateTime={shipment.deliveryPlannedDateTime} />
    ),
  },
  {
    id: 'related-orders',
    element: ({ shipmentId, relatedOrders = [] }) => {
      const orderCount = relatedOrders.length;
      const label = Math.abs(orderCount) === 1 ? 'Order' : 'Orders';
      return (
        <NavigateButton
          isDisabled={!orderCount}
          text={orderCount ? `${orderCount} ${label}` : 'No Order'}
          to={{
            pathname: `/results/${PATHS.relatedView}`,
            search: `?relatedView=shipment:${shipmentId}`,
          }}
        />
      );
    },
  },
  {
    id: 'current-stop',
    element: ({ currentStopLocation }) => (
      <LocationCell
        locationName={currentStopLocation?.name || '-'}
        cityState={currentStopLocation?.address?.getCityState() || ''}
      />
    ),
  },
  {
    id: 'connection-status',
    element: ({ latestTrackingStatusEvent }) => {
      const connectivity = latestTrackingStatusEvent?.type;
      if (connectivity) {
        return (
          <Pill
            icon={CONNECTIVITY_ICON_MAP[connectivity]}
            label={CONNECTIVITY_TITLE_MAP[connectivity]}
          />
        );
      } else {
        return '-';
      }
    },
  },
  {
    id: 'shared-with',
    element: ({ sharingContext }) => {
      return (
        <CompactKeyList
          namespace={t('core.sharedWith')}
          data={sharingContext?.recipients || []}
          getKey={(item) => item.tenantUuid}
          getLabel={(item) => item.tenantName}
        />
      );
    },
  },
  {
    id: 'visibility-provided',
    element: ({ sharingContext }) => {
      return (
        <CompactKeyList
          namespace={t('core.visibilityProvidedBy')}
          data={sharingContext?.dataOriginators || []}
          getKey={(item) => item.tenantUuid}
          getLabel={(item) => item.tenantName}
        />
      );
    },
  },
  {
    id: 'poo-port-name',
    element: buildLocationCellForMilestone('DEPARTURE_FROM_FIRST_PORT'),
  },
  {
    id: 'poo-departure-time',
    element: buildEtaOrActCellForMilestone('DEPARTURE_FROM_FIRST_PORT'),
  },
  {
    id: 'poo-planned-departure',
    element: buildDateCellForMilestone('DEPARTURE_FROM_FIRST_PORT'),
  },
  {
    id: 'pod-port-name',
    element: buildLocationCellForMilestone('DISCHARGE_AT_LAST_PORT'),
  },
  {
    id: 'pod-est-act-arrival',
    element: buildEtaOrActCellForMilestone('DISCHARGE_AT_LAST_PORT'),
  },
  {
    id: 'pod-planned-arrival',
    element: buildDateCellForMilestone('DISCHARGE_AT_LAST_PORT'),
  },
];

export const SHIPMENT_TABLE_HEADERS = [
  { id: 'shipment-id', label: t('core.shipmentId'), width: '220px' },
  { id: 'current-state', label: t('core.currentState'), width: '140px' },
  { id: 'current-carrier', label: t('core.currentCarrier'), width: '160px' },
  { id: 'origin', label: t('core.origin'), width: '220px' },
  {
    id: 'est-act-departure',
    label: t('core.departureTime'),
    width: '330px',
    options: DEPARTURE_SORT_OPTIONS,
  },
  {
    id: 'planned-departure',
    label: t('core.plannedDeparture'),
    width: '240px',
    options: PLANNED_SORT_OPTIONS_DEPARTURE,
  },
  { id: 'destination', label: t('core.destination'), width: '220px' },
  {
    id: 'est-act-arrival',
    label: t('core.arrivalTime'),
    width: '330px',
    options: ARRIVAL_SORT_OPTIONS,
  },
  {
    id: 'planned-arrival',
    label: t('core.plannedArrival'),
    width: '240px',
    options: PLANNED_SORT_OPTIONS_ARRIVAL,
  },
  {
    id: 'related-orders',
    label: t('core.relatedOrders'),
    width: '160px',
  },
  { id: 'current-stop', label: t('core.currentStop'), width: '220px' },
  { id: 'connection-status', label: t('core.connectivity'), width: '220px' },
  { id: 'shared-with', label: t('core.sharedWith'), width: '220px' },
  {
    id: 'visibility-provided',
    label: t('core.visibilityProvidedBy'),
    width: '220px',
  },
  {
    id: 'poo-port-name',
    label: t('core.portOfOrigin'),
    width: '240px',
  },
  {
    id: 'poo-departure-time',
    label: t('sort.departureTime'),
    width: '260px',
    options: POO_DEPARTURE_SORT_OPTIONS,
  },
  {
    id: 'poo-planned-departure',
    label: t('sort.plannedDeparture'),
    width: '220px',
    options: POO_PLANNED_SORT_OPTIONS_DEPARTURE,
  },
  {
    id: 'pod-port-name',
    width: '240px',
    label: t('core.portOfDischarge'),
  },
  {
    id: 'pod-est-act-arrival',
    label: t('sort.dischargeTime'),
    width: '330px',
    options: POD_ARRIVAL_SORT_OPTIONS,
  },
  {
    id: 'pod-planned-arrival',
    label: t('sort.plannedDischarge'),
    width: '240px',
    options: POD_PLANNED_SORT_OPTIONS_ARRIVAL,
  },
];
